import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import CompositeDialog from 'react-components/dialog/CompositeDialog';
import Loader from '@act/react-components/workflows/Loader';
import { membersAppSelectors } from 'redux/slices/memberSlice.slice';
import { consentRequests } from 'redux/requests/consents.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { themeColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import './link-consents-dialog.scss';
import { Alert, Button } from '@act/storybook';

export interface LinkConsentsDialogUniqueProps {
  userId: string;
}
export type LinkConsentsDialogCallbackInput = void;

export type LinkConsentsDialogProps =
  GenericDialogProps<LinkConsentsDialogCallbackInput> &
    LinkConsentsDialogUniqueProps;

export const LinkConsentsDialog = ({
  userId,
  open,
  setOpen,
  callback,
}: LinkConsentsDialogProps) => {
  const user = membersAppSelectors.useSelectMemberById(userId);
  const [copied, setCopied] = useState(false);
  const [linkResponse, isLinkLoading, createConsentLink] = useApiCall(
    consentRequests.createConsentLink
  );
  useEffect(() => {
    if (open) {
      createConsentLink({ params: null, body: { userId } });
    }
  }, [open, userId]);

  function handleCopy() {
    setCopied(true);
    void navigator.clipboard.writeText(linkText);
    setTimeout(() => {
      setCopied(false);
    }, 3000); // should match css fade time
  }

  const linkText = linkResponse?.url;
  const handleClose = () => {
    setOpen(false);
    callback();
  };

  return (
    <CompositeDialog
      handleClose={handleClose}
      open={open}
      title={'Copy consents link'}
    >
      {isLinkLoading ? (
        <Loader />
      ) : (
        <div className="dialog-content-area">
          <Typography variant="titleMedium">
            This link is personalized for {user?.name} to fill out the Reema
            Enrollment Agreement Consents.
          </Typography>
          <div className="link-area">
            <Box
              sx={{
                backgroundColor: themeColors.inverseOnSurface,
                padding: '8px',
                flexGrow: 1,
                marginRight: '8px',
              }}
            >
              <Typography variant="bodyMedium">{linkText}</Typography>
            </Box>
            <Button trackingName="copy-button"
              onClick={handleCopy}
              startIcon={copied ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
              sx={{
                width: '150px',
              }}
            >
              {copied ? (
                <Typography className="copied-message-fade">
                  Link copied
                </Typography>
              ) : (
                <Typography>Copy link</Typography>
              )}
            </Button>
          </div>
          <Alert
            severity="warning"
            variant="tonal"
            trackingName="link-consents-sharing-alert"
          >
            {`Only share this link with ${user?.name}`}
          </Alert>
        </div>
      )}
    </CompositeDialog>
  );
};
